import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { Search, MapPin, Calendar, IndianRupee, Filter, Users, Megaphone, Heart, HeartOff } from 'lucide-react';

export default function BrowseBriefs() {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    method: '',
    sector: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [methods, setMethods] = useState([]);
  const [sectors, setSectors] = useState([]);
  
  useEffect(() => {
    const fetchConstants = async () => {
      try {
        const [methodsRes, sectorsRes] = await Promise.all([
          api.get('/constants/methods'),
          api.get('/constants/sectors')
        ]);
        setMethods(methodsRes.data.methods || []);
        setSectors(sectorsRes.data.sectors || []);
      } catch (error) {
        console.error('Error fetching constants:', error);
      }
    };
    fetchConstants();
  }, []);
  
  const fetchBriefs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filters.method) params.append('method', filters.method);
      if (filters.sector) params.append('sector', filters.sector);
      
      const response = await api.get(`/briefs?${params.toString()}`);
      setBriefs(response.data.briefs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching briefs:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchBriefs();
  }, []);
  
  const handleSearch = (e) => {
    e.preventDefault();
    fetchBriefs();
  };
  
  const toggleInterest = async (briefId, hasInterest) => {
    try {
      if (hasInterest) {
        await api.delete(`/briefs/${briefId}/interest`);
      } else {
        await api.post(`/briefs/${briefId}/interest`, { brief_id: briefId });
      }
      fetchBriefs();
    } catch (error) {
      console.error('Error toggling interest:', error);
      alert(error.response?.data?.detail || 'Failed to update interest');
    }
  };
  
  const formatBudget = (min, max) => {
    if (!min && !max) return null;
    const formatNum = (n) => n >= 100000 ? `₹${(n/100000).toFixed(1)}L` : `₹${(n/1000).toFixed(0)}K`;
    if (min && max) return `${formatNum(min)} - ${formatNum(max)}`;
    if (max) return `Up to ${formatNum(max)}`;
    return `From ${formatNum(min)}`;
  };
  
  return (
    <DashboardLayout title="Browse Briefs">
      <p className="text-neutral-600 mb-6">
        Explore research briefs from clients. Express interest to get shortlisted for projects.
      </p>
      
      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
            <input
              type="text"
              data-testid="search-input"
              placeholder="Search briefs by title, company, or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-neutral-200 bg-white focus:border-[#FF6B35] outline-none"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-3 border ${showFilters ? 'border-[#FF6B35] bg-[#FFF0EB]' : 'border-neutral-200 bg-white'} flex items-center gap-2`}
          >
            <Filter size={20} />
            Filters
          </button>
          <button type="submit" className="btn-researcher px-6 py-3">
            Search
          </button>
        </div>
      </form>
      
      {showFilters && (
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Method</label>
              <select
                value={filters.method}
                onChange={(e) => setFilters(f => ({ ...f, method: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-200 bg-white"
              >
                <option value="">All Methods</option>
                {methods.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Sector</label>
              <select
                value={filters.sector}
                onChange={(e) => setFilters(f => ({ ...f, sector: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-200 bg-white"
              >
                <option value="">All Sectors</option>
                {sectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={fetchBriefs} className="btn-researcher px-6 py-2">Apply</button>
          </div>
        </div>
      )}
      
      <div className="text-sm text-neutral-500 mb-4">{total} briefs found</div>
      
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div>
        </div>
      ) : briefs.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-12 text-center">
          <Megaphone className="text-neutral-300 mx-auto mb-4" size={48} />
          <p className="text-neutral-500">No briefs available</p>
        </div>
      ) : (
        <div className="space-y-4">
          {briefs.map((brief) => (
            <div
              key={brief.brief_id}
              data-testid={`brief-card-${brief.brief_id}`}
              className="bg-white border border-neutral-200 p-6 card-researcher hover-lift"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold px-2 py-0.5 bg-purple-100 text-purple-700">BRIEF</span>
                    {brief.deadline && (
                      <span className="text-xs text-neutral-500">
                        Deadline: {brief.deadline}
                      </span>
                    )}
                  </div>
                  
                  <Link to={`/brief/${brief.brief_id}`}>
                    <h3 className="font-semibold text-lg text-neutral-900 hover:text-[#FF6B35] transition-colors mb-1">
                      {brief.title}
                    </h3>
                  </Link>
                  
                  <p className="text-sm text-neutral-500 mb-2">
                    {brief.company_name || brief.client?.name}
                  </p>
                  
                  <p className="text-neutral-600 text-sm mb-4 line-clamp-2">
                    {brief.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-3 text-xs text-neutral-500">
                    {brief.location && (
                      <span className="flex items-center gap-1">
                        <MapPin size={12} />
                        {brief.location}
                      </span>
                    )}
                    {brief.timeline && (
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {brief.timeline}
                      </span>
                    )}
                    {formatBudget(brief.budget_min, brief.budget_max) && (
                      <span className="flex items-center gap-1">
                        <IndianRupee size={12} />
                        {formatBudget(brief.budget_min, brief.budget_max)}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Users size={12} />
                      {brief.interest_count} interested
                    </span>
                  </div>
                  
                  <div className="flex gap-2 mt-3">
                    {brief.method && (
                      <span className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-600">{brief.method}</span>
                    )}
                    {brief.sector && (
                      <span className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-600">{brief.sector}</span>
                    )}
                  </div>
                </div>
                
                <button
                  data-testid={`interest-btn-${brief.brief_id}`}
                  onClick={() => toggleInterest(brief.brief_id, brief.has_expressed_interest)}
                  className={`p-2.5 rounded-sm transition-colors ${
                    brief.has_expressed_interest
                      ? 'bg-[#FF6B35] text-white'
                      : 'bg-neutral-100 text-neutral-500 hover:bg-[#FFF0EB] hover:text-[#FF6B35]'
                  }`}
                  title={brief.has_expressed_interest ? 'Withdraw Interest' : 'Express Interest'}
                >
                  {brief.has_expressed_interest ? <Heart size={20} fill="currentColor" /> : <Heart size={20} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
